import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Button,
  Alert,
  Table,
  Badge,
  ProgressBar,
  Spinner,
  Row,
  Col,
  Modal,
  Nav,
  Tab,
  InputGroup
} from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { getProducts } from '../../services/productApi';
import { getItems, updateItem } from '../../services/itemApi';
import { updateProduct } from '../../services/productApi';
import { getCategoriesByType } from '../../services/categoryApi';
import { dotoniwoofApi } from '../../api/dotoniwooApi';
import {
  generateBulkEditProductsExcel,
  generateBulkEditItemsExcel,
  parseBulkEditProductsExcel,
  parseBulkEditItemsExcel,
  downloadBulkEditExcel
} from '../../utils/bulkEditExcelGenerator';

const BULK_EDIT_FIELDS = {
  products: [
    { key: 'name', label: 'Nombre', type: 'text', placeholder: 'Nuevo nombre del producto' },
    { key: 'description', label: 'Descripción', type: 'textarea', placeholder: 'Nueva descripción' },
    { key: 'id_category', label: 'Categoría', type: 'select', placeholder: 'Seleccionar categoría' }
  ],
  items: [
    { key: 'description', label: 'Descripción', type: 'text', placeholder: 'Nueva descripción del item' },
    { key: 'sku', label: 'SKU', type: 'text', placeholder: 'Nuevo SKU' },
    { key: 'price', label: 'Precio', type: 'number', placeholder: '0' },
    { key: 'stock', label: 'Stock', type: 'number', placeholder: '0' },
    { key: 'id_color', label: 'Color', type: 'select', placeholder: 'Seleccionar color' },
    { key: 'id_size', label: 'Talla', type: 'select', placeholder: 'Seleccionar talla' },
    { key: 'id_weight', label: 'Gramaje', type: 'select', placeholder: 'Seleccionar gramaje' }
  ]
};

const BulkEditTab = ({ showAlert, onEditComplete }) => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('products');
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editField, setEditField] = useState('');
  const [editValue, setEditValue] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [editResults, setEditResults] = useState({ success: 0, errors: [] });

  // Datos para selects
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [weights, setWeights] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Excel bulk edit
  const [excelFile, setExcelFile] = useState(null);
  const [generatingExcel, setGeneratingExcel] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [categoriesForExcel, setCategoriesForExcel] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsRes, itemsRes, colorsRes, sizesRes, weightsRes, typesRes] = await Promise.all([
        getProducts(),
        getItems(),
        dotoniwoofApi.get('/color.php'),
        dotoniwoofApi.get('/size.php'),
        dotoniwoofApi.get('/weight.php'),
        dotoniwoofApi.get('/categoryType.php')
      ]);

      if (productsRes.ok && productsRes.data?.code === 200) {
        const raw = productsRes.data.message;
        setProducts(Array.isArray(raw) ? raw : raw?.products || []);
      }
      if (itemsRes.ok && itemsRes.data?.code === 200) {
        const raw = itemsRes.data.message;
        setItems(Array.isArray(raw) ? raw : raw?.items || []);
      }
      if (colorsRes.data?.code === 200) setColors(colorsRes.data.message || []);
      if (sizesRes.data?.code === 200) setSizes(sizesRes.data.message || []);
      if (weightsRes.data?.code === 200) setWeights(weightsRes.data.message || []);
      if (typesRes.data?.code === 200) setCategoryTypes(typesRes.data.message || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
      showAlert('Error al cargar los datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editField === 'id_category' && categoryTypes.length > 0) {
      loadAllCategories();
    }
  }, [editField, categoryTypes]);

  const loadAllCategories = async () => {
    setLoadingCategories(true);
    try {
      const allCats = [];
      for (const type of categoryTypes) {
        try {
          const result = await getCategoriesByType(type.id_category_type);
          const raw = result.data?.message || [];
          const list = Array.isArray(raw) ? raw : raw?.categories || [];
          allCats.push(
            ...list.map((c) => ({
              ...c,
              typeName: type.categoryTypeName
            }))
          );
        } catch (e) {
          console.error('Error cargando categorías:', e);
        }
      }
      setCategories(allCats);
      setCategoriesForExcel(allCats);
      return allCats;
    } catch (error) {
      setCategories([]);
      return [];
    } finally {
      setLoadingCategories(false);
    }
  };

  const filteredProducts = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    if (!term) return products;
    return products.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(term) ||
        (p.description || '').toLowerCase().includes(term) ||
        (p.category_name || '').toLowerCase().includes(term)
    );
  }, [products, searchTerm]);

  const filteredItems = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    if (!term) return items;
    return items.filter(
      (i) =>
        (i.description || '').toLowerCase().includes(term) ||
        (i.sku || '').toLowerCase().includes(term) ||
        (i.product_name || i.name || '').toLowerCase().includes(term)
    );
  }, [items, searchTerm]);

  const toggleSelectProduct = (id) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectItem = (id) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllProducts = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map((p) => p.id_product)));
    }
  };

  const selectAllItems = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map((i) => i.id_item)));
    }
  };

  const openEditModal = () => {
    setEditField('');
    setEditValue('');
    setShowEditModal(true);
  };

  const handleBulkEditProducts = async () => {
    if (!editField || selectedProducts.size === 0) return;

    let value = editValue;
    if (editField === 'id_category') value = editValue ? parseInt(editValue, 10) : null;
    if (editField === 'description') value = (editValue || '').trim();
    if (editField === 'name') value = (editValue || '').trim();

    if (editField === 'name' && !value) {
      showAlert('El nombre no puede estar vacío', 'error');
      return;
    }
    if (editField === 'id_category' && !value) {
      showAlert('Selecciona una categoría', 'error');
      return;
    }

    setUpdating(true);
    setUpdateProgress(0);
    const results = { success: 0, errors: [] };
    const total = selectedProducts.size;
    let done = 0;

    for (const id of selectedProducts) {
      try {
        const payload = { firebase_UID: user.uid };
        if (editField === 'name') payload.name = value;
        else if (editField === 'description') payload.description = value;
        else if (editField === 'id_category') payload.id_category = value;

        const prod = products.find((p) => p.id_product === id);
        if (prod) {
          payload.name = payload.name ?? prod.name;
          payload.description = payload.description ?? prod.description;
          payload.id_category = payload.id_category ?? prod.id_category;
        }

        const result = await updateProduct(id, payload);
        if (result.ok && (result.data?.code === 200 || result.data?.code === 204)) {
          results.success++;
        } else {
          results.errors.push({ id, name: prod?.name, message: result.data?.message || 'Error' });
        }
      } catch (error) {
        const prod = products.find((p) => p.id_product === id);
        results.errors.push({
          id,
          name: prod?.name,
          message: error?.response?.data?.message || error?.message || 'Error desconocido'
        });
      }
      done++;
      setUpdateProgress((done / total) * 100);
    }

    setEditResults(results);
    setShowResults(true);
    setShowEditModal(false);
    setSelectedProducts(new Set());
    setUpdating(false);
    if (onEditComplete) onEditComplete();
    showAlert(
      results.errors.length === 0
        ? `${results.success} productos actualizados correctamente`
        : `${results.success} actualizados, ${results.errors.length} con errores`,
      results.errors.length === 0 ? 'success' : 'warning'
    );
  };

  const handleBulkEditItems = async () => {
    if (!editField || selectedItems.size === 0) return;

    let value = editValue;
    if (editField === 'price') value = parseFloat(editValue);
    else if (editField === 'stock') value = parseInt(editValue, 10);
    else if (['id_color', 'id_size', 'id_weight'].includes(editField)) value = editValue ? parseInt(editValue, 10) : undefined;
    else if (['description', 'sku'].includes(editField)) value = (editValue || '').trim();

    setUpdating(true);
    setUpdateProgress(0);
    const results = { success: 0, errors: [] };
    const total = selectedItems.size;
    let done = 0;

    for (const id of selectedItems) {
      try {
        const item = items.find((i) => i.id_item === id);
        const payload = {
          firebase_UID: user.uid,
          description: item?.description,
          sku: item?.sku,
          price: item?.price,
          stock: item?.stock,
          id_color: item?.id_color,
          id_size: item?.id_size,
          id_weight: item?.id_weight
        };

        if (editField === 'description') payload.description = value;
        else if (editField === 'sku') payload.sku = value || undefined;
        else if (editField === 'price') payload.price = value;
        else if (editField === 'stock') payload.stock = value;
        else if (editField === 'id_color') payload.id_color = value;
        else if (editField === 'id_size') payload.id_size = value;
        else if (editField === 'id_weight') payload.id_weight = value;

        const result = await updateItem(id, payload);
        if (result.ok && (result.data?.code === 200 || result.data?.code === 204)) {
          results.success++;
        } else {
          results.errors.push({
            id,
            name: item?.description || item?.sku,
            message: result.data?.message || 'Error'
          });
        }
      } catch (error) {
        const item = items.find((i) => i.id_item === id);
        results.errors.push({
          id,
          name: item?.description || item?.sku,
          message: error?.response?.data?.message || error?.message || 'Error desconocido'
        });
      }
      done++;
      setUpdateProgress((done / total) * 100);
    }

    setEditResults(results);
    setShowResults(true);
    setShowEditModal(false);
    setSelectedItems(new Set());
    setUpdating(false);
    if (onEditComplete) onEditComplete();
    showAlert(
      results.errors.length === 0
        ? `${results.success} ítems actualizados correctamente`
        : `${results.success} actualizados, ${results.errors.length} con errores`,
      results.errors.length === 0 ? 'success' : 'warning'
    );
  };

  const handleApplyEdit = () => {
    if (activeSection === 'products') handleBulkEditProducts();
    else handleBulkEditItems();
  };

  const handleDownloadExcel = async () => {
    setGeneratingExcel(true);
    try {
      if (activeSection === 'products') {
        const cats = categories.length > 0 ? categories : await loadAllCategories();
        const workbook = await generateBulkEditProductsExcel(products, cats);
        await downloadBulkEditExcel(workbook, 'editar_productos.xlsx');
        showAlert('Excel descargado. Las columnas con 🔒 están bloqueadas.', 'success');
      } else {
        const workbook = await generateBulkEditItemsExcel(items, colors, sizes, weights);
        await downloadBulkEditExcel(workbook, 'editar_items.xlsx');
        showAlert('Excel descargado. Las columnas con 🔒 están bloqueadas.', 'success');
      }
    } catch (error) {
      console.error('Error generando Excel:', error);
      showAlert('Error al generar el archivo Excel', 'error');
    } finally {
      setGeneratingExcel(false);
    }
  };

  const handleUploadExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      showAlert('Solo se permiten archivos Excel (.xlsx)', 'error');
      return;
    }
    setExcelFile(file);
    setUploadingExcel(true);
    setUpdating(true);
    const results = { success: 0, errors: [] };
    try {
      if (activeSection === 'products') {
        const rows = await parseBulkEditProductsExcel(file);
        if (rows.length === 0) {
          showAlert('El Excel no contiene datos en la hoja "Productos"', 'error');
          setUploadingExcel(false);
          setUpdating(false);
          event.target.value = '';
          return;
        }
        const cats = categories.length > 0 ? categories : await loadAllCategories();
        const total = rows.length;
        let done = 0;
        for (const row of rows) {
          try {
            const cat = cats.find((c) => (c.category_name || '').toLowerCase() === (row.categoria || '').toLowerCase());
            const prod = products.find((p) => p.id_product === row.id_product);
            const idCategory = cat?.id_category || prod?.id_category;
            if (!idCategory) {
              results.errors.push({ id: row.id_product, name: row.name, message: `Categoría "${row.categoria}" no encontrada` });
            } else {
              const result = await updateProduct(row.id_product, {
                firebase_UID: user.uid,
                name: row.name || prod?.name || '',
                description: row.description ?? prod?.description ?? '',
                id_category: idCategory
              });
              if (result.ok && (result.data?.code === 200 || result.data?.code === 204)) {
                results.success++;
              } else {
                results.errors.push({ id: row.id_product, name: row.name, message: result.data?.message || 'Error' });
              }
            }
          } catch (err) {
            results.errors.push({ id: row.id_product, name: row.name, message: err?.message || 'Error' });
          }
          done++;
          setUpdateProgress((done / total) * 100);
        }
      } else {
        const rows = await parseBulkEditItemsExcel(file);
        if (rows.length === 0) {
          showAlert('El Excel no contiene datos en la hoja "Items"', 'error');
          setUploadingExcel(false);
          setUpdating(false);
          event.target.value = '';
          return;
        }
        const total = rows.length;
        let done = 0;
        for (const row of rows) {
          try {
            const colorId = !row.color || row.color === 'N/A' ? undefined : colors.find((c) => (c.color_name || '').toLowerCase() === row.color.toLowerCase())?.id_color;
            const sizeId = !row.talla || row.talla === 'N/A' ? undefined : sizes.find((s) => (s.size_name || '').toLowerCase() === row.talla.toLowerCase())?.id_size;
            const weightId = !row.gramaje || row.gramaje === 'N/A' ? undefined : weights.find((w) => (w.weight_name || '').toLowerCase() === row.gramaje.toLowerCase())?.id_weight;
            const result = await updateItem(row.id_item, {
              firebase_UID: user.uid,
              description: row.description || '',
              sku: row.sku || undefined,
              price: row.precio,
              stock: row.stock,
              id_color: colorId,
              id_size: sizeId,
              id_weight: weightId
            });
            if (result.ok && (result.data?.code === 200 || result.data?.code === 204)) {
              results.success++;
            } else {
              results.errors.push({ id: row.id_item, name: row.description, message: result.data?.message || 'Error' });
            }
          } catch (err) {
            results.errors.push({ id: row.id_item, name: row.description, message: err?.message || 'Error' });
          }
          done++;
          setUpdateProgress((done / total) * 100);
        }
      }
      setEditResults(results);
      setShowResults(true);
      showAlert(results.errors.length === 0 ? `${results.success} registros actualizados` : `${results.success} actualizados, ${results.errors.length} errores`, results.errors.length === 0 ? 'success' : 'warning');
      if (onEditComplete) onEditComplete();
    } catch (error) {
      console.error('Error procesando Excel:', error);
      showAlert(error?.message || 'Error al procesar el archivo Excel', 'error');
    } finally {
      setUploadingExcel(false);
      setUpdating(false);
      setUpdateProgress(0);
      event.target.value = '';
    }
  };

  useEffect(() => {
    if (categories.length > 0 && categoriesForExcel.length === 0) {
      setCategoriesForExcel(categories);
    }
  }, [categories]);

  const selectedCount = activeSection === 'products' ? selectedProducts.size : selectedItems.size;
  const fields = BULK_EDIT_FIELDS[activeSection];

  const getSelectOptions = () => {
    if (editField === 'id_category') return categories;
    if (editField === 'id_color') return colors;
    if (editField === 'id_size') return sizes;
    if (editField === 'id_weight') return weights;
    return [];
  };

  const getSelectIdField = () => {
    if (editField === 'id_category') return 'id_category';
    if (editField === 'id_color') return 'id_color';
    if (editField === 'id_size') return 'id_size';
    if (editField === 'id_weight') return 'id_weight';
    return 'id';
  };

  const getSelectNameField = () => {
    if (editField === 'id_category') return 'category_name';
    if (editField === 'id_color') return 'color_name';
    if (editField === 'id_size') return 'size_name';
    if (editField === 'id_weight') return 'weight_name';
    return 'name';
  };

  return (
    <>
      <Card className="modern-card bulk-edit-card">
        <Card.Header className="modern-card-header bulk-edit-header">
          <h4 className="mb-0">
            <i className="fas fa-edit me-2"></i>
            Edición Masiva
          </h4>
          <p className="mb-0 mt-1 small opacity-90">
            Selecciona varios productos o ítems y actualiza un campo en todos a la vez
          </p>
        </Card.Header>
        <Card.Body>
          {/* Editar con Excel */}
          <Card className="bulk-edit-excel-card mb-4">
            <Card.Header className="bulk-edit-excel-header">
              <h5 className="mb-0">
                <i className="fas fa-file-excel me-2"></i>
                Editar con Excel
              </h5>
              <p className="mb-0 mt-1 small opacity-90">
                Descarga un Excel con tus datos actuales. Las columnas con llaves están bloqueadas.
              </p>
            </Card.Header>
            <Card.Body>
              <div className="d-flex flex-wrap gap-3 align-items-center">
                <Button
                  variant="outline-success"
                  size="lg"
                  onClick={handleDownloadExcel}
                  disabled={loading || generatingExcel || (activeSection === 'products' && products.length === 0) || (activeSection === 'items' && items.length === 0)}
                >
                  {generatingExcel ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-download me-2"></i>
                      Descargar Excel {activeSection === 'products' ? 'Productos' : 'Items'}
                    </>
                  )}
                </Button>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Control
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleUploadExcel}
                    disabled={loading || uploadingExcel}
                    className="bulk-edit-excel-input d-none"
                    id="excelBulkEditInput"
                  />
                  <Button
                    as="label"
                    variant="primary"
                    className="modern-btn-primary"
                    htmlFor="excelBulkEditInput"
                    disabled={loading || uploadingExcel}
                    style={{ cursor: loading || uploadingExcel ? 'not-allowed' : 'pointer' }}
                  >
                    {uploadingExcel ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-upload me-2"></i>
                        Subir Excel editado
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <Alert variant="info" className="mt-3 mb-0">
                <i className="fas fa-lock me-2"></i>
                <strong>Columnas bloqueadas (🔒):</strong>{' '}
                {activeSection === 'products'
                  ? 'id_product - No modifiques estas columnas, solo edita nombre, descripcion y categoria'
                  : 'id_item, id_product, producto - Edita descripcion, sku, precio, stock, color, talla y gramaje'}
              </Alert>
            </Card.Body>
          </Card>

          <hr className="my-4 bulk-edit-divider" />

          <h6 className="mb-3">
            <i className="fas fa-mouse-pointer me-2"></i>
            O edita seleccionando aquí
          </h6>
          <Nav variant="pills" className="bulk-edit-nav mb-4">
            <Nav.Item>
              <Nav.Link
                eventKey="products"
                active={activeSection === 'products'}
                onClick={() => {
                  setActiveSection('products');
                  setSearchTerm('');
                }}
                className="bulk-edit-nav-link"
              >
                <i className="fas fa-box me-2"></i>
                Productos
                <Badge bg="secondary" className="ms-2">
                  {products.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="items"
                active={activeSection === 'items'}
                onClick={() => {
                  setActiveSection('items');
                  setSearchTerm('');
                }}
                className="bulk-edit-nav-link"
              >
                <i className="fas fa-cube me-2"></i>
                Items
                <Badge bg="secondary" className="ms-2">
                  {items.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <div className="bulk-edit-search mb-3">
            <InputGroup className="bulk-edit-search-input">
              <InputGroup.Text>
                <i className="fas fa-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={
                  activeSection === 'products'
                    ? 'Buscar productos por nombre, descripción o categoría...'
                    : 'Buscar items por descripción, SKU o producto...'
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>

          {selectedCount > 0 && (
            <div className="bulk-edit-toolbar animate-fade-in">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <Badge bg="primary" className="bulk-edit-badge fs-6">
                  <i className="fas fa-check-square me-1"></i>
                  {selectedCount} seleccionado{selectedCount !== 1 ? 's' : ''}
                </Badge>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={activeSection === 'products' ? selectAllProducts : selectAllItems}
                >
                  {activeSection === 'products'
                    ? selectedProducts.size === filteredProducts.length
                      ? 'Desmarcar todos'
                      : 'Seleccionar todos'
                    : selectedItems.size === filteredItems.length
                    ? 'Desmarcar todos'
                    : 'Seleccionar todos'}
                </Button>
                <Button
                  variant="primary"
                  className="modern-btn-primary"
                  onClick={openEditModal}
                  disabled={updating}
                >
                  <i className="fas fa-edit me-2"></i>
                  Editar seleccionados
                </Button>
                <Button
                  variant="link"
                  className="text-muted"
                  onClick={() => (activeSection === 'products' ? setSelectedProducts(new Set()) : setSelectedItems(new Set()))}
                >
                  Limpiar selección
                </Button>
              </div>
            </div>
          )}

          {updating && (
            <div className="bulk-edit-progress mb-4">
              <ProgressBar
                now={updateProgress}
                label={`${Math.round(updateProgress)}%`}
                animated
                variant="success"
                className="bulk-edit-progress-bar"
              />
              <div className="text-center mt-2">
                <Spinner animation="border" size="sm" className="me-2" />
                Actualizando...
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2">Cargando datos...</p>
            </div>
          ) : activeSection === 'products' ? (
            filteredProducts.length === 0 ? (
              <div className="bulk-edit-empty">
                <div className="bulk-edit-empty-icon">
                  <i className="fas fa-box-open"></i>
                </div>
                <p className="text-muted">
                  {products.length === 0 ? 'No hay productos. Crea algunos primero.' : 'No hay resultados para la búsqueda.'}
                </p>
              </div>
            ) : (
              <div className="table-responsive bulk-edit-table-wrap">
                <Table hover className="modern-table bulk-edit-table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>
                        <Form.Check
                          type="checkbox"
                          checked={selectedProducts.size === filteredProducts.length && filteredProducts.length > 0}
                          onChange={selectAllProducts}
                        />
                      </th>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th>Categoría</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((prod) => (
                      <tr key={prod.id_product} className={selectedProducts.has(prod.id_product) ? 'row-selected' : ''}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedProducts.has(prod.id_product)}
                            onChange={() => toggleSelectProduct(prod.id_product)}
                          />
                        </td>
                        <td className="fw-semibold">{prod.name}</td>
                        <td>{(prod.description || '-').slice(0, 50)}{(prod.description || '').length > 50 ? '…' : ''}</td>
                        <td>
                          <Badge bg="secondary">{prod.category_name || '-'}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )
          ) : filteredItems.length === 0 ? (
            <div className="bulk-edit-empty">
              <div className="bulk-edit-empty-icon">
                <i className="fas fa-cubes"></i>
              </div>
              <p className="text-muted">
                {items.length === 0 ? 'No hay ítems. Crea algunos primero.' : 'No hay resultados para la búsqueda.'}
              </p>
            </div>
          ) : (
            <div className="table-responsive bulk-edit-table-wrap">
              <Table hover className="modern-table bulk-edit-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
                        onChange={selectAllItems}
                      />
                    </th>
                    <th>SKU</th>
                    <th>Producto</th>
                    <th>Descripción</th>
                    <th>Precio</th>
                    <th>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => (
                    <tr key={item.id_item} className={selectedItems.has(item.id_item) ? 'row-selected' : ''}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedItems.has(item.id_item)}
                          onChange={() => toggleSelectItem(item.id_item)}
                        />
                      </td>
                      <td className="fw-semibold">{item.sku || item.id_item || '-'}</td>
                      <td>{item.product_name || item.name || '-'}</td>
                      <td>{(item.description || '-').slice(0, 40)}{(item.description || '').length > 40 ? '…' : ''}</td>
                      <td>{item.price != null ? `$ ${Number(item.price).toLocaleString('es-CO')}` : '-'}</td>
                      <td>{item.stock != null ? item.stock : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal de edición */}
      <Modal
        show={showEditModal}
        onHide={() => !updating && setShowEditModal(false)}
        centered
        className="bulk-edit-modal"
      >
        <Modal.Header closeButton className="bulk-edit-modal-header">
          <Modal.Title>
            <i className="fas fa-edit me-2"></i>
            Editar {selectedCount} {activeSection === 'products' ? 'producto(s)' : 'ítem(s)'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">
              <i className="fas fa-tag me-2"></i>
              Campo a actualizar
            </Form.Label>
            <Form.Select
              value={editField}
              onChange={(e) => {
                setEditField(e.target.value);
                setEditValue('');
                if (e.target.value === 'id_category') {
                  loadAllCategories();
                }
              }}
              className="modern-select"
            >
              <option value="">Selecciona un campo...</option>
              {fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {editField && (
            <Form.Group>
              <Form.Label className="fw-semibold">
                Nuevo valor para &quot;{fields.find((f) => f.key === editField)?.label}&quot;
              </Form.Label>
              {['id_category', 'id_color', 'id_size', 'id_weight'].includes(editField) ? (
                <Form.Select
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="modern-select"
                  disabled={loadingCategories}
                >
                  <option value="">
                    {loadingCategories ? 'Cargando...' : 'Seleccionar...'}
                  </option>
                  {getSelectOptions().map((opt) => {
                    const idVal = opt[getSelectIdField()];
                    const nameVal = opt[getSelectNameField()];
                    const typeLabel = opt.typeName ? ` (${opt.typeName})` : '';
                    return (
                      <option key={idVal} value={idVal}>
                        {nameVal}{typeLabel}
                      </option>
                    );
                  })}
                </Form.Select>
              ) : (
                <Form.Control
                  type={fields.find((f) => f.key === editField)?.type || 'text'}
                  as={fields.find((f) => f.key === editField)?.type === 'textarea' ? 'textarea' : 'input'}
                  rows={fields.find((f) => f.key === editField)?.type === 'textarea' ? 3 : undefined}
                  placeholder={fields.find((f) => f.key === editField)?.placeholder}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="modern-input"
                />
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={updating}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            className="modern-btn-primary"
            onClick={handleApplyEdit}
            disabled={!editField || updating}
          >
            {updating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Aplicando...
              </>
            ) : (
              <>
                <i className="fas fa-check me-2"></i>
                Aplicar a {selectedCount} {activeSection === 'products' ? 'producto(s)' : 'ítem(s)'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de resultados */}
      <Modal show={showResults} onHide={() => setShowResults(false)} centered size="lg">
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="fas fa-check-circle me-2"></i>
            Resultados de la edición
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success">
            <h5>
              <i className="fas fa-check me-2"></i>
              {editResults.success} registros actualizados correctamente
            </h5>
          </Alert>

          {editResults.errors.length > 0 && (
            <>
              <Alert variant="danger">
                <h5>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {editResults.errors.length} errores
                </h5>
              </Alert>
              <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      <th>Nombre/ID</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editResults.errors.map((err, idx) => (
                      <tr key={idx}>
                        <td>{err.name || err.id}</td>
                        <td className="text-danger">{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowResults(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BulkEditTab;
